import { redirect, LoaderFunctionArgs } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { ISeason } from "types";

/**
 * Loader for /:org route (no season in URL)
 * If seasons are cached, redirect immediately to /:org/:season
 * Otherwise, return null and let the component handle loading + redirect
 */
export function createSeasonRedirectLoader(queryClient: QueryClient) {
  return async ({ params }: LoaderFunctionArgs) => {
    if (!params.org) {
      throw new Error("Organization parameter is required");
    }
    // The loader has no organization list to canonicalize against, but
    // useSeasonsByOrg keys its cache on the canonical slug. Lowercasing keeps
    // a mixed-case deep link from missing the cache on every visit and from
    // redirecting to a non-canonical URL that OrgWrapper then has to rewrite
    // a second time (CUL-746).
    const org = params.org.toLowerCase();

    // Check if seasons are in cache (from useSeasonsByOrg hook)
    const cachedSeasons = queryClient.getQueryData<ISeason[]>([
      "organizations",
      org,
      "seasons",
    ]);

    if (cachedSeasons?.length) {
      // Seasons are cached - redirect immediately to first season
      return redirect(`/${org}/${cachedSeasons[0].name}`);
    }

    // No cache - return null, component will handle loading + redirect
    return null;
  };
}
